import { CdkDragDrop, moveItemInArray } from "@angular/cdk/drag-drop";
import {Component, Input, OnInit} from "@angular/core";
import { ToastrService } from "ngx-toastr";
import { Store } from "../../../../services/store.service";
import {TranslatedBaseError} from "../../TranslatedBaseError";
import {TranslateService} from "@ngx-translate/core";
import {DynamicValidationRow} from "../../../../models/dynamic-validation-row";
import {ComponentValidationControllerService} from "../../../../services/form-controller/component-validation-controller.service";
import {ComponentValidation} from "../../../../models/component-validation";
import {
  DeterminantControllerService,
} from "../../../../services/registration-controller/determinant-controller.service";

@Component({
  selector: "dynamic-validation",
  templateUrl: "./dynamic-validation.component.html",
  styles: [],
  providers: [],
})
export class DynamicValidationComponent extends TranslatedBaseError implements OnInit {

  @Input() public serviceId: string;
  @Input() public componentValidationId: string;
  @Input() public componentKey: string;
  public determinantMapById: { [key: string]: string } = {};

  public dynamicValidation: ComponentValidation = new ComponentValidation();
  public newRow: DynamicValidationRow = new DynamicValidationRow();
  public selectedRow: DynamicValidationRow = new DynamicValidationRow();
  public showDeterminantBuilder: boolean = false;
  public selectedIndex: number;
  public listToggle: boolean = false;
  public tempRow: any = null;
  public showDrawerHeader: boolean = true;
  public tempDeterminant: any = null;

  constructor(private componentDynamicValidationControllerService: ComponentValidationControllerService,
              public store: Store,
              public determinantService: DeterminantControllerService,
              toastr: ToastrService, public translateService: TranslateService) {
    super(toastr, translateService);
  }

  public ngOnInit(): void {
    this.load();
    this.getServiceDeterminantKeyName();
  }

  public getServiceDeterminantKeyName() {
    this.determinantService.getServiceDeterminantIdAndName(this.serviceId).subscribe(
      (data) => Object.assign(this.determinantMapById, data),
    );
  }

  public dropped(event: CdkDragDrop<any[]>) {
    if (event.previousContainer === event.container) {
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
      this.save();
    }
  }

  public fixIndexes() {
    this.dynamicValidation.validationRows.forEach((it, index) => {
      it.sortOrder = index;
    });
  }

  public showModalHeader(e) {
    console.log(e);
    this.showDrawerHeader = e;
  }

  public addNewRow(): void {
    if (!this.dynamicValidation) {
      this.dynamicValidation = new ComponentValidation();
    }
    const copy: DynamicValidationRow = JSON.parse(JSON.stringify(this.newRow));
    this.dynamicValidation.validationRows.push(copy);
    this.newRow = new DynamicValidationRow();
    this.save();
  }

  public isAddButtonDisabled(): boolean {
    return !this.newRow.validationMessage;
  }

  public openDeterminantModal(row: DynamicValidationRow) {
    this.selectedRow = row;
    this.showDeterminantBuilder = true;
    this.showAsFullDrawer();
  }

  public closeDeterminantLayer() {
    this.showDeterminantBuilder = false;
    this.tempDeterminant = null;
    this.backAsFramedDrawer();
  }

  public closeEdit() {
    this.store.state.common.determinantSection.topLayer = "list";
    this.store.state.common.determinantSection.determinant = {};
  }

  public setListView(b: boolean) {
    this.listToggle = b;
  }

  public isDeterminantEmpty(jsonDeterminant: string): boolean {
    if (jsonDeterminant) {
      const dets = JSON.parse(jsonDeterminant)[0].items;
      return !(dets && dets.length > 0);
    }
    return true;
  }

  private load() {
    const cb = (data) => {
      this.dynamicValidation = data || new ComponentValidation();
      if (!this.dynamicValidation.validationRows?.length) {
        this.dynamicValidation.validationRows = [];
      }
      this.dynamicValidation.validationRows.sort((a, b) => a.sortOrder - b.sortOrder);
    };
    if (this.componentValidationId) {
      this.componentDynamicValidationControllerService.getById(this.componentValidationId).subscribe(
        cb, (error) => this.showErrorMessage(error));
    } else {
      this.componentDynamicValidationControllerService.get(this.serviceId, this.componentKey).subscribe(
        cb, (error) => this.showErrorMessage(error));
    }
  }

  public updateDeterminant(event: any) {
    if (!this.tempDeterminant || this.isDeterminantEmpty(this.tempDeterminant.jsonString)) {
      this.closeDeterminantLayer();
      return;
    }
    this.selectedRow.jsonDeterminants = this.tempDeterminant.jsonString;
    if (this.newRow !== this.selectedRow) { this.save(); } // trigger save for existing rows
  }

  private static sendSaveProcessingEvent(state = true) {
    window.top.postMessage([{channel: "FormBuilder", message: JSON.stringify({event: "saveProcessing", state})}], "*");
  }

  public disableSave() {
    DynamicValidationComponent.sendSaveProcessingEvent();
  }

  private sendMessage(): void {
    const message = {
      key: this.componentKey, count: this.dynamicValidation.validationRows.length,
      changes: {
        componentValidationId: this.dynamicValidation.id, validationRowIds: this.dynamicValidation.validationRows.map((a) => a.id),
      },
    };
    const messageString: string = JSON.stringify(message);
    window.top.postMessage([{channel: "FormBuilder", message: messageString}], "*");
  }

  public save() {
    DynamicValidationComponent.sendSaveProcessingEvent();
    this.fixIndexes();
    const cb = (data) => {
      data?.validationRows?.sort((a, b) => a.sortOrder - b.sortOrder);
      Object.assign(this.dynamicValidation, data);
      this.componentValidationId = this.dynamicValidation.id;
      this.responseSuccessful("Component updated!");
      this.closeDeterminantLayer();
      this.sendMessage();
      DynamicValidationComponent.sendSaveProcessingEvent(false);
    };
    const errorCb = (error) => {
      this.showErrorMessage(error);
      this.closeDeterminantLayer();
      DynamicValidationComponent.sendSaveProcessingEvent(false);
    };
    if (this.componentValidationId) {
      this.componentDynamicValidationControllerService.saveById(this.componentValidationId, this.dynamicValidation)
        .subscribe(cb, errorCb);
    } else {
      this.componentDynamicValidationControllerService.save(this.serviceId, this.componentKey, this.dynamicValidation)
        .subscribe(cb, errorCb);
    }

  }

  public deleteRow(index: number): void {
    if (index > -1) {
      this.dynamicValidation.validationRows.splice(index, 1);
      this.save();
    }
  }

  public showAsFullDrawer() {
    const windowParent = window.parent.document.querySelector(".component-settings")?.querySelector(".formio-dialog-content > div > div");
    if (windowParent) {
      windowParent.querySelectorAll("iframe")?.forEach(iframe => iframe.classList.add("full-drawer"));
      windowParent.querySelector(".page-iframe")?.classList.add("full-drawer-content");
      windowParent.querySelector(":scope > .row:nth-child(1)")?.classList.add("d-none");
      windowParent.querySelector(":scope > .row:nth-child(2)")?.classList.add("p-0");
      // windowParent.querySelector(":scope > .row:nth-child(2)")?.classList.add("d-none");
      windowParent.querySelector(":scope > .row:nth-child(2)")?.querySelector("#myBuilder").classList.add("m-0");
      windowParent.querySelector(":scope > .row:nth-child(2)")?.querySelector(".tab-pane.active").classList.add("p-0", "m-0");
      // windowParent.querySelector(":scope > .row:last-child")?.classList.add("d-none");
      window.parent.document.querySelector(".component-settings")?.querySelector(".formio-dialog-close")?.classList.add("d-none");
      windowParent.querySelector("#element1")?.querySelector(":scope > .formio-component-tabs")?.querySelector(":scope > div > .nav-tabs")?.classList.add("d-none");
      windowParent.querySelector("#validation")?.querySelector(":scope > .formio-component-validationSubTabs")?.querySelector(":scope > div > .nav-tabs")?.classList.add("d-none");
    }
  }

  public backAsFramedDrawer() {
    const windowParent = window.parent.document.querySelector(".component-settings")?.querySelector(".formio-dialog-content > div > div");
    if (windowParent) {
      windowParent.querySelectorAll("iframe")?.forEach(iframe => iframe.classList.remove("full-drawer") );
      windowParent.querySelector(".page-iframe")?.classList.remove("full-drawer-content");
      windowParent.querySelector(":scope > .row:nth-child(1)")?.classList.remove("d-none");
      windowParent.querySelector(":scope > .row:nth-child(2)")?.classList.remove("p-0");
      // windowParent.querySelector(":scope > .row:nth-child(2)")?.classList.remove("d-none");
      windowParent.querySelector(":scope > .row:nth-child(2)")?.querySelector("#myBuilder").classList.remove("m-0");
      windowParent.querySelector(":scope > .row:nth-child(2)")?.querySelector(".tab-pane.active")?.classList.remove("p-0", "m-0");
      // windowParent.querySelector(":scope > .row:last-child")?.classList.remove("d-none");
      window.parent.document.querySelector(".component-settings")?.querySelector(".formio-dialog-close")?.classList.remove("d-none");
      windowParent.querySelector("#element1")?.querySelector(":scope > .formio-component-tabs")?.querySelector(":scope > div > .nav-tabs")?.classList.remove("d-none");
      windowParent.querySelector("#validation")?.querySelector(":scope > .formio-component-validationSubTabs")?.querySelector(":scope > div > .nav-tabs")?.classList.remove("d-none");
    }
  }
}
